import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./adminAuth.server";
import {
  DEVICE_KEYS,
  MARKET_KEYS,
  PAGE_KEYS,
  isPageKeyLike,
  makeVariantKey,
  type PageVariants,
} from "./pageBackgrounds.shared";
import {
  createEmptyPageBackgroundMap,
  extractPublicMediaPath,
  normalizePageBackgroundEntry,
  normalizePageBackgroundSettings,
} from "./pageBackgrounds.server";

/** يقرأ الخريطة الخام (مفاتيح ثابتة + نطاقات منتجات `p-<handle>`). */
function readMap(raw: unknown): Record<string, PageVariants> {
  const map = createEmptyPageBackgroundMap() as Record<string, PageVariants>;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (value !== undefined && isPageKeyLike(key)) map[key] = normalizePageBackgroundEntry(value);
    }
  }
  return map;
}

export const getPageBackgrounds = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const [main, legacy] = await Promise.all([
    supabaseAdmin.from("app_settings").select("value").eq("key", "page_backgrounds").maybeSingle(),
    supabaseAdmin.from("app_settings").select("value").eq("key", "account_background_url").maybeSingle(),
  ]);
  const rawMain = main.data?.value;
  const map = readMap(rawMain);
  const accountTouched =
    !!rawMain && typeof rawMain === "object" && Object.prototype.hasOwnProperty.call(rawMain, "account");
  if (!accountTouched && !map.account?.["all:all"]?.url && legacy.data?.value) {
    map.account = { ...(map.account ?? {}), "all:all": normalizePageBackgroundSettings(legacy.data.value) };
  }
  return { map };
});

export const setPageBackground = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({
      pageKey: z.string().max(140).refine(isPageKeyLike, "مفتاح صفحة غير صالح"),
      device: z.enum(DEVICE_KEYS).default("all"),
      market: z.enum(MARKET_KEYS).default("all"),
      url: z.string().max(2000).nullable().optional(),
      color: z.string().max(32).nullable().optional(),
      size: z.enum(["cover", "contain", "100%", "auto"]).optional(),
      posX: z.number().min(0).max(100).optional(),
      posY: z.number().min(0).max(100).optional(),
      mode: z.enum(["fixed", "parallax_tall"]).optional(),
    }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await assertAdmin(context.userId);
    const current = await supabaseAdmin.from("app_settings").select("value").eq("key", "page_backgrounds").maybeSingle();
    const map = readMap(current.data?.value);
    const variantKey = makeVariantKey(data.device, data.market);
    const newUrl = (data.url ?? "").trim();
    const newColor = (data.color ?? "").trim();
    if (!map[data.pageKey]) map[data.pageKey] = {};
    const oldUrl = map[data.pageKey]?.[variantKey]?.url ?? "";
    if (!newUrl && !newColor) {
      delete map[data.pageKey][variantKey];
    } else {
      map[data.pageKey][variantKey] = {
        url: newUrl,
        color: newColor,
        size: data.size ?? "cover",
        posX: data.posX ?? 50,
        posY: data.posY ?? 50,
        mode: data.mode ?? "fixed",
      };
    }
    const serialized: Record<string, { variants: PageVariants }> = {};
    for (const key of new Set([...PAGE_KEYS, ...Object.keys(map)])) {
      serialized[key] = { variants: map[key] ?? {} };
    }
    const { error } = await supabaseAdmin.from("app_settings").upsert({
      key: "page_backgrounds",
      value: serialized,
      updated_by: context.userId,
      updated_at: new Date().toISOString(),
    }, { onConflict: "key" });
    if (error) throw new Error(error.message);
    if (data.pageKey === "account" && variantKey === "all:all") {
      await supabaseAdmin.from("app_settings").delete().eq("key", "account_background_url");
    }
    const oldPath = extractPublicMediaPath(oldUrl);
    const newPath = extractPublicMediaPath(newUrl);
    if (oldPath && oldPath !== newPath) {
      const stillUsed = Object.keys(map).some((pageKey) =>
        Object.values(map[pageKey] ?? {}).some((settings) => extractPublicMediaPath(settings?.url) === oldPath),
      );
      if (!stillUsed) await supabaseAdmin.storage.from("public-media").remove([oldPath]).catch(() => {});
    }
    return { ok: true, map };
  });
